import React from 'react'
import { Spin } from 'antd'
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom'
import RouteGuard from '@/components/RouteGuard/RouteGuard'
import PermissionGuard from '@/components/RouteGuard/PermissionGuard'
import { lazyWithRetry } from '@/utils/lazyWithRetry'

const LoginPage = lazyWithRetry(() => import('@/pages/Login'))
const AppLayout = lazyWithRetry(() => import('@/components/Layout/AppLayout'))
const Dashboard = lazyWithRetry(() => import('@/pages/Dashboard'))
const MenuManagementPage = lazyWithRetry(() => import('@/pages/System/Menu'))
const RoleManagementPage = lazyWithRetry(() => import('@/pages/System/Role'))
const AdminManagementPage = lazyWithRetry(() => import('@/pages/System/Admin'))
const SystemSettingsPage = lazyWithRetry(() => import('@/pages/System/Settings'))
const SystemLogsPage = lazyWithRetry(() => import('@/pages/System/Logs'))
const EmailTemplatePage = lazyWithRetry(() => import('@/pages/Campaign/Template'))
const AudienceListPage = lazyWithRetry(() => import('@/pages/Campaign/AudienceList'))
const AudienceListDetailPage = lazyWithRetry(() => import('@/pages/Campaign/AudienceList/Detail'))
const CampaignTaskPage = lazyWithRetry(() => import('@/pages/Campaign/Task'))
const CampaignTaskDetailPage = lazyWithRetry(() => import('@/pages/Campaign/Task/Detail'))
const SuppressionPage = lazyWithRetry(() => import('@/pages/Campaign/Suppression'))
const PostmarkEventPage = lazyWithRetry(() => import('@/pages/Mail/PostmarkEvent'))
const InboundEmailPage = lazyWithRetry(() => import('@/pages/Mail/InboundEmail'))
const InboundEmailDetailPage = lazyWithRetry(() => import('@/pages/Mail/InboundEmail/Detail'))
const SendingAccountPage = lazyWithRetry(() => import('@/pages/Mail/SendingAccount'))
const NotFoundPage = lazyWithRetry(() => import('@/pages/Error/NotFoundPage'))

/** Full-screen centered spinner used as Suspense fallback for top-level routes */
const FullPageSpin = (
  <div className="flex items-center justify-center w-full h-screen">
    <Spin size="large" />
  </div>
)

/** Content-area centered spinner used as Suspense fallback for child routes */
const ContentSpin = (
  <div className="flex items-center justify-center w-full h-full min-h-64">
    <Spin size="large" />
  </div>
)

/** Wraps a page element with PermissionGuard + Suspense */
function PageTransition({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()

  return (
    <div key={pathname} className="h-full" style={{ animation: 'page-enter 0.3s ease-out both' }}>
      {children}
    </div>
  )
}

function guarded(element: React.ReactNode) {
  return (
    <PermissionGuard>
      <React.Suspense fallback={ContentSpin}>
        <PageTransition>{element}</PageTransition>
      </React.Suspense>
    </PermissionGuard>
  )
}

/**
 * 基座路由表：登录、Dashboard、系统管理五页与错误页。
 *
 * 业务模块页面在 children 中登记，路径需与后端 `admin_menus` 的 `url` 一致 ——
 * `PermissionGuard` 依据菜单树判定访问权限，未登记进菜单的路径会被判为无权访问。
 */
export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <React.Suspense fallback={FullPageSpin}>
        <LoginPage />
      </React.Suspense>
    ),
  },
  {
    path: '/',
    element: (
      <RouteGuard>
        <React.Suspense fallback={FullPageSpin}>
          <AppLayout />
        </React.Suspense>
      </RouteGuard>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: guarded(<Dashboard />) },
      { path: 'system/menu', element: guarded(<MenuManagementPage />) },
      { path: 'system/role', element: guarded(<RoleManagementPage />) },
      { path: 'system/admin', element: guarded(<AdminManagementPage />) },
      { path: 'system/settings', element: guarded(<SystemSettingsPage />) },
      { path: 'system/logs', element: guarded(<SystemLogsPage />) },
      { path: 'campaign/templates', element: guarded(<EmailTemplatePage />) },
      { path: 'campaign/lists', element: guarded(<AudienceListPage />) },
      { path: 'campaign/lists/:id', element: guarded(<AudienceListDetailPage />) },
      { path: 'campaign/tasks', element: guarded(<CampaignTaskPage />) },
      { path: 'campaign/tasks/:id', element: guarded(<CampaignTaskDetailPage />) },
      { path: 'campaign/suppressions', element: guarded(<SuppressionPage />) },
      { path: 'mail/postmark-events', element: guarded(<PostmarkEventPage />) },
      { path: 'mail/inbound-emails', element: guarded(<InboundEmailPage />) },
      { path: 'mail/inbound-emails/:id', element: guarded(<InboundEmailDetailPage />) },
      { path: 'mail/sending-accounts', element: guarded(<SendingAccountPage />) },
      // Catch-all: any path inside the layout that isn't registered → 404
      {
        path: '*',
        element: (
          <React.Suspense fallback={ContentSpin}>
            <NotFoundPage />
          </React.Suspense>
        ),
      },
    ],
  },
  // Catch-all outside the layout (e.g. /some-random-top-level)
  {
    path: '*',
    element: (
      <React.Suspense fallback={FullPageSpin}>
        <NotFoundPage />
      </React.Suspense>
    ),
  },
])
